// src/pwa/src/views/FinanceView/impl/DocumentActionsBar.tsx
// Issue 74 — per-document Send / Save / Download action bar reused by Quotes,
// Invoices, Budget rows and Reports. Keeps the brand style consistent across
// the four surfaces and centralises the toast/feedback logic.

import React, { useState } from 'react';
import { Mail, MessageCircle, Save, Download, Loader2 } from 'lucide-react';
import { downloadPdf } from '../../../services/pdfService';
import { sendEmail } from '../../../services/emailService';
import { sendWhatsApp } from '../../../services/whatsappService';
import { savePdfToRepo } from '../../../services/documentsRepoService';

export type DocKind = 'quote' | 'invoice' | 'budget' | 'report';

export interface DocumentActionsBarProps {
    kind: DocKind;
    reference: string;
    pdf: Blob;
    clientEmail?: string;
    clientPhone?: string;
    clientName?: string;
    documentLabel?: string;
    onAfterAction?: (action: 'email' | 'whatsapp' | 'save' | 'download', result: { url?: string; queued?: boolean }) => void;
    compact?: boolean;
}

interface ToastState {
    message: string;
    tone: 'info' | 'success' | 'error';
}

export const DocumentActionsBar: React.FC<DocumentActionsBarProps> = ({
    kind,
    reference,
    pdf,
    clientEmail,
    clientPhone,
    clientName,
    documentLabel,
    onAfterAction,
    compact = false,
}) => {
    const [busy, setBusy] = useState<string | null>(null);
    const [toast, setToast] = useState<ToastState | null>(null);
    const label = documentLabel || reference;

    const showToast = (message: string, tone: ToastState['tone'] = 'info') => {
        setToast({ message, tone });
        window.setTimeout(() => setToast(null), 3000);
    };

    const filename = `${kind}-${reference.replace(/[^A-Za-z0-9._-]/g, '-')}.pdf`;
    const subjectLine = `${label}`;
    const bodyLine = clientName
        ? `Hi ${clientName}, please find your ${kind} attached. Regards, BizBuilder Platform`
        : `Please find your ${kind} attached. Regards, BizBuilder Platform`;

    const handleDownload = async () => {
        setBusy('download');
        try {
            downloadPdf(pdf, filename);
            showToast('Downloaded.', 'success');
            onAfterAction?.('download', {});
        } finally {
            setBusy(null);
        }
    };

    const handleEmail = async () => {
        setBusy('email');
        try {
            const res = await sendEmail({
                to: clientEmail,
                subject: subjectLine,
                body: bodyLine,
                attachment: { filename, pdf },
            });
            showToast(
                res.method === 'api' ? 'Email queued via SMTP.' : 'Opened mail client — attach the downloaded PDF.',
                res.method === 'api' ? 'success' : 'info',
            );
            onAfterAction?.('email', {});
        } catch {
            showToast('Failed to send email.', 'error');
        } finally {
            setBusy(null);
        }
    };

    const handleWhatsApp = async () => {
        setBusy('whatsapp');
        try {
            await sendWhatsApp({
                phone: clientPhone,
                body: bodyLine,
                pdfLink: { filename, pdf },
            });
            showToast('WhatsApp message opened — PDF link is included.', 'success');
            onAfterAction?.('whatsapp', {});
        } catch {
            showToast('Failed to open WhatsApp.', 'error');
        } finally {
            setBusy(null);
        }
    };

    const handleSave = async () => {
        setBusy('save');
        try {
            const result = await savePdfToRepo({ type: kind, reference, pdf, clientName });
            if (result.queued) {
                showToast(`Saved offline — will retry upload to ${result.path}.`, 'info');
            } else {
                showToast(`Saved to ${result.path}.`, 'success');
            }
            onAfterAction?.('save', { url: result.url, queued: result.queued });
        } catch {
            showToast('Failed to save to repo.', 'error');
        } finally {
            setBusy(null);
        }
    };

    const sizeCls = compact
        ? 'p-1.5 text-xs font-bold rounded-lg'
        : 'px-3 py-1.5 text-xs font-bold rounded-xl';

    const Btn: React.FC<{
        name: string;
        title: string;
        icon: React.ReactNode;
        onClick: () => void;
        color: string;
    }> = ({ name, title, icon, onClick, color }) => (
        <button
            type="button"
            title={title}
            aria-label={title}
            disabled={busy !== null}
            onClick={onClick}
            className={`flex items-center gap-1 ${sizeCls} ${color} hover:opacity-90 disabled:opacity-50 transition-all`}
        >
            {busy === name ? <Loader2 size={12} className="animate-spin" /> : icon}
            {!compact && <span className="uppercase tracking-widest">{name}</span>}
       </button>
    );

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <Btn name="email" title="Send as PDF via Email" icon={<Mail size={12} />} onClick={handleEmail} color="bg-blue-50 text-blue-700" />
                <Btn name="whatsapp" title="Send as PDF via WhatsApp" icon={<MessageCircle size={12} />} onClick={handleWhatsApp} color="bg-emerald-50 text-emerald-700" />
                <Btn name="save" title="Save PDF to repo/GitHub" icon={<Save size={12} />} onClick={handleSave} color="bg-amber-50 text-amber-700" />
                <Btn name="download" title="Download PDF" icon={<Download size={12} />} onClick={handleDownload} color="bg-slate-100 text-slate-700" />
           </div>
            {toast && (
                <div
                    className={`text-[10px] font-bold uppercase tracking-widest rounded-lg px-3 py-2 ${
                        toast.tone === 'success' ? 'bg-emerald-50 text-emerald-700' :
                        toast.tone === 'error' ? 'bg-red-50 text-red-700' :
                        'bg-slate-50 text-slate-700'
                    }`}
                >
                    {toast.message}
               </div>
            )}
       </div>
    );
};

export default DocumentActionsBar;
